import { Children, cloneElement, type HTMLAttributes, isValidElement, useId } from 'react';
import type { FormControlProps } from '../FormControl';
import { useValue } from '../../hooks/useValue';
import type { CheckboxProps } from './Checkbox';
import clsx from 'clsx';

export interface CheckboxesProps<Value = string>
  extends Pick<
      FormControlProps<Value[]>,
      | 'id'
      | 'name'
      | 'value'
      | 'onChange'
      | 'disabled'
      | 'readOnly'
      | 'tabIndex'
      | 'description'
      | 'validationStatus'
      | 'errorMessage'
    >,
    Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {}

export function Checkboxes<Value = string>(props: CheckboxesProps<Value>) {
  const internalId = useId();

  const {
    id = internalId,
    name = id,
    children,
    value: initialValue,
    defaultValue,
    onChange,
    disabled,
    readOnly,
    tabIndex = 0,
    ...rest
  } = props;

  const { value, setValue } = useValue<Value[]>({
    name,
    initialValue,
    formatter(value) {
      return value !== undefined ? ([].concat(value as never) as Value[]) : value;
    },
    defaultValue: (defaultValue as Value[]) || ([] as Value[]),
    onChange,
  });

  return (
    <div {...rest} className={rest.className} role="listbox">
      {Children.map(children, (child, index) => {
        if (isValidElement(child)) {
          const propValue: Value = (child.props as CheckboxProps<Value>).value as Value;

          return cloneElement(child, {
            ...(child.props as any),
            name,
            id: `${id}-${index}`,
            disabled,
            readOnly,
            tabIndex: tabIndex + index + 1,
            className: clsx((child.props as any).className || 'mt-8 first:mt-0'),
            checked: value.includes(propValue),
            onChange(_: string, item: Value | null) {
              if (item === null) {
                setValue(value.filter((v) => v !== propValue));
              } else {
                setValue(value.concat(propValue));
              }
            },
          } as CheckboxProps<Value>);
        }

        return child;
      })}
    </div>
  );
}
